import { combineQueries, getEntityType } from '@datorama/akita';
import { UIModelCallback } from '@y42/feature/git/state';
import { EMPTY_CHILD_NAME, EMPTY_CHILD_PATH } from '@y42/feature/shared/list-view';
import { ImpexQuery } from '@y42/impex';
import { DatasourceTypeEnum, Y42FeatureType } from '@y42/json-schema';
import { combineLatest, debounceTime, filter, map, Observable, shareReplay } from 'rxjs';
import { DatasourceTableUIModel } from '../datasource-table/datasource-table.model';
import { DatasourceTableQuery } from '../datasource-table/datasource-table.query';
import { DatasourceCTypeModel } from '../datasource-type';
import { DatasourceTypeQuery } from '../datasource-type/datasource-type-query.service';
import { DatasourceListViewData, DatasourceState, DatasourceUIModel } from './datasource.model';
import { DatasourceStore } from './datasource.store';

export abstract class DatasourceQuery<
  State extends DatasourceState = DatasourceState,
  UIModel extends DatasourceUIModel = DatasourceUIModel,
  ChildUIModel extends DatasourceTableUIModel = DatasourceTableUIModel,
> extends ImpexQuery<State, UIModel, ChildUIModel, DatasourceListViewData> {
  constructor(
    protected readonly featureType: Y42FeatureType,
    protected readonly store: DatasourceStore<State>,
    protected readonly datasourceTypeQuery: DatasourceTypeQuery,
    protected readonly datasourceTableQuery: DatasourceTableQuery,
    protected readonly settings: { toUIModel: UIModelCallback<State, UIModel> },
  ) {
    super({
      store,
      featureType,
      toUIModel: settings.toUIModel,
    });
  }

  abstract readonly types$: Observable<DatasourceCTypeModel[] | null>;

  readonly datasourceTypes$ = this.datasourceTypeQuery.datasourceTypes$.pipe(filter((st) => (st || []).length > 0));

  readonly items$ = this.getItems$();

  readonly listItems$: Observable<DatasourceListViewData[]> = combineLatest([this.loading$, this.items$]).pipe(
    filter(([loading]) => !loading),
    map(([_, datasources]) => datasources.map(this.toListViewModel)),
    map((arr) => arr.flat()),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  readonly active$ = this.getActive$();

  readonly isFileType$: Observable<boolean> = this.active$.pipe(
    map((datasource) => datasource as DatasourceUIModel | undefined),
    map((datasource) => {
      if (datasource?.typeModel?.type) {
        return [DatasourceTypeEnum.Xlsx, DatasourceTypeEnum.Csv].includes(datasource.typeModel?.type);
      }
      return false;
    }),
  );

  readonly activeTables$ = this.active$.pipe(
    map((datasource) => datasource as DatasourceUIModel | undefined),
    map((datasource) => datasource?.tables || []),
  );

  private _items$?: Observable<UIModel[]>;
  private _active$?: Observable<UIModel | undefined>;

  protected getItems$() {
    if (!this._items$) {
      this._items$ = combineQueries([
        this.selectAll(),
        this.datasourceTableQuery.selectAll(),
        this.datasourceTypes$,
        this.select((st) => st.last_used),
        this.select((st) => st.starred),
      ]).pipe(
        debounceTime(0),
        map(([datasources]) => datasources.map(this.toUIModel)),
        shareReplay({ bufferSize: 1, refCount: true }),
      );
    }

    return this._items$;
  }

  protected getActive$() {
    if (!this._active$) {
      this._active$ = combineQueries([
        this.selectActive() as Observable<getEntityType<State> | undefined>,
        this.datasourceTableQuery.selectAll(),
        this.datasourceTypes$,
        this.select((st) => st.last_used),
        this.select((st) => st.starred),
      ]).pipe(
        debounceTime(0),
        map(([active]) => (active ? (this.toUIModel(active) as UIModel) : undefined)),
        shareReplay({ bufferSize: 1, refCount: true }),
      );
    }

    return this._active$;
  }

  readonly toListViewModel = (datasource: UIModel): DatasourceListViewData[] => {
    const baseRow: DatasourceListViewData = {
      ...datasource,
      id: datasource.$path,
      rows: 0,
      child_$path: EMPTY_CHILD_PATH,
      child_name: EMPTY_CHILD_NAME,
      child_status: null,
      child_description: '',
      data_test_count: 0,
    };

    if (!datasource.tables?.length) {
      return [baseRow];
    }

    return datasource.tables.map((table): DatasourceListViewData => {
      const lastSuccessfulJob = table.latest_valid_job;
      return {
        ...baseRow,
        child_$path: table.$path,
        child_name: table.name,
        child_status: table.latest_job?.status || null,
        child_description: table.description,
        rows: lastSuccessfulJob?.details?.row_count || 0,
        last_import_date: lastSuccessfulJob?.finished_at ? new Date(lastSuccessfulJob?.finished_at).valueOf() : null,
        data_test_count: table.tests?.tests.length ?? 0,
      };
    });
  };
}
